/**
 * KhiTodayPlanCard — „Моят план за днес" в бъбречното здраве.
 * Пътят на пациента: 4-6 конкретни задачи, изградени от неговите данни.
 * Данни: GET /api/khi/today-plan
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Circle, Loader2, ShieldCheck, ChevronRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KhiTodayPlanCard({ cardCls, isDark, onGo }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/khi/today-plan`);
      setD(data);
    } catch {
      setD(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const again = () => load();
    window.addEventListener("khi:acr:saved", again);
    return () => window.removeEventListener("khi:acr:saved", again);
  }, [load]);

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="khi-today-loading">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-500 mx-auto" />
      </div>
    );
  }
  if (!d) return null;

  return (
    <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="khi-today-plan-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-base font-extrabold">Моят план за днес</h2>
          <p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}
            data-testid="khi-today-headline">
            {d.headline_bg}
          </p>
        </div>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-cyan-500/15 text-cyan-500"
          data-testid="khi-today-progress">
          {d.done_count} от {d.total} готови
        </span>
      </div>

      <div className={`mt-3 h-2 rounded-full overflow-hidden ${isDark ? "bg-white/10" : "bg-slate-200"}`}>
        <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all"
          style={{ width: `${d.progress_pct}%` }} />
      </div>

      <div className="mt-3 space-y-1.5" data-testid="khi-today-tasks">
        {d.tasks.map((t) => (
          <button key={t.code} onClick={() => onGo?.(t)}
            className={`w-full text-left p-2.5 rounded-xl border transition-all flex items-start gap-2.5 ${
              t.done
                ? (isDark ? "border-emerald-500/30 bg-emerald-500/[0.07]" : "border-emerald-200 bg-emerald-50")
                : t.priority === "high"
                  ? (isDark ? "border-amber-500/40 bg-amber-500/[0.08] hover:bg-amber-500/[0.14]"
                            : "border-amber-300 bg-amber-50 hover:bg-amber-100")
                  : (isDark ? "border-white/10 hover:bg-white/5" : "border-slate-200 hover:bg-slate-50")
            }`}
            data-testid={`khi-today-task-${t.code}`}>
            {t.done
              ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              : <Circle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />}
            <span className="flex-1 min-w-0">
              <span className={`block text-[13px] font-bold ${t.done ? "opacity-70" : ""}`}>
                {t.title_bg}
              </span>
              <span className="block text-[11px] opacity-70 leading-snug">{t.why_bg}</span>
              {t.detail_bg && (
                <span className="block text-[10px] mt-0.5 font-semibold opacity-80"
                  data-testid={`khi-today-detail-${t.code}`}>
                  {t.detail_bg}
                </span>
              )}
            </span>
            {!t.done && <ChevronRight className="w-4 h-4 opacity-50 shrink-0 mt-0.5" />}
          </button>
        ))}
      </div>

      <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-white/[0.04]" : "bg-slate-50"}`}
        data-testid="khi-today-prevention">
        <div className="text-[11px] font-bold inline-flex items-center gap-1 mb-1">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
          Превенция за Вашия стадий{d.stage ? ` (${d.stage})` : ""}
        </div>
        <ul className="space-y-1">
          {d.prevention_bg.map((p, i) => (
            <li key={i} className="text-[11px] flex items-start gap-1.5">
              <Sparkles className="w-3 h-3 text-cyan-500 shrink-0 mt-0.5" />
              <span className="opacity-85">{p}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="text-[9px] opacity-50 mt-2">{d.disclaimer_bg}</div>
    </div>
  );
}
